import { useState, useEffect, useRef } from "react";

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, isInView];
}

function FadeIn({ children, delay = 0, className = "" }) {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.8s cubic-bezier(0.25,0.46,0.45,0.94) ${delay}s, transform 0.8s cubic-bezier(0.25,0.46,0.45,0.94) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

export default function UntetheredGiving() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", color: "#2D2D2D", background: "#FAF8F5" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,700;1,400&display=swap');

        *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }

        .nav-fixed {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          transition: all 0.4s ease;
        }
        .nav-scrolled {
          background: rgba(250, 248, 245, 0.95);
          backdrop-filter: blur(20px);
          box-shadow: 0 1px 0 rgba(45,74,62,0.08);
        }
        .nav-top { background: transparent; }

        .hero-section {
          min-height: 100vh;
          background: linear-gradient(165deg, #2D4A3E 0%, #1a3029 40%, #2D4A3E 100%);
          position: relative;
          overflow: hidden;
          display: flex;
          align-items: center;
        }
        .hero-section::before {
          content: '';
          position: absolute;
          top: -50%; left: -50%;
          width: 200%; height: 200%;
          background: radial-gradient(ellipse at 30% 20%, rgba(196,167,125,0.08) 0%, transparent 50%),
                      radial-gradient(ellipse at 70% 80%, rgba(90,138,128,0.06) 0%, transparent 50%);
          animation: drift 20s ease-in-out infinite;
        }
        @keyframes drift {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(-2%, 1%) rotate(1deg); }
        }
        .hero-section::after {
          content: '';
          position: absolute; inset: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C4A77D' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          opacity: 0.5;
        }

        .playfair { font-family: 'Playfair Display', Georgia, 'Times New Roman', serif; }
        .dm { font-family: 'DM Sans', -apple-system, sans-serif; }

        .gold-line {
          width: 60px; height: 2px;
          background: #C4A77D;
          display: block;
        }

        .cta-btn {
          display: inline-block;
          padding: 16px 40px;
          background: #C4A77D;
          color: #fff;
          font-family: 'DM Sans', sans-serif;
          font-weight: 500;
          font-size: 15px;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
        }
        .cta-btn:hover {
          background: #b8976d;
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(196,167,125,0.3);
        }
        .cta-outline {
          background: transparent;
          border: 1.5px solid #C4A77D;
          color: #C4A77D;
        }
        .cta-outline:hover {
          background: #C4A77D;
          color: #fff;
        }

        .nav-link {
          color: inherit;
          text-decoration: none;
          font-size: 13px;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          font-weight: 500;
          cursor: pointer;
          transition: color 0.3s;
          background: none;
          border: none;
          font-family: 'DM Sans', sans-serif;
        }
        .nav-link:hover { color: #C4A77D; }

        .section-pad {
          padding: 100px 24px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .process-step {
          position: relative;
          padding-left: 80px;
        }
        .process-step::before {
          content: attr(data-step);
          position: absolute;
          left: 0; top: -4px;
          width: 52px; height: 52px;
          border-radius: 50%;
          background: #FAF8F5;
          border: 2px solid #C4A77D;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Playfair Display', serif;
          font-size: 20px;
          color: #2D4A3E;
          font-weight: 600;
        }

        .mobile-menu {
          display: none;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
        }
        
        .about-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }
        
        .founder-grid {
          display: grid;
          grid-template-columns: 180px 1fr;
          gap: 40px;
          align-items: start;
          max-width: 800px;
          margin: 40px auto 0;
        }
        
        .founder-photo {
          width: 180px;
          height: 220px;
          border-radius: 12px;
          overflow: hidden;
          border: 2px solid #C4A77D;
        }
        
        .trust-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
          max-width: 800px;
          margin: 48px auto 0;
        }
        
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu { display: block; }
          .mobile-nav {
            position: fixed;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(45,74,62,0.97);
            z-index: 200;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 32px;
          }
          .mobile-nav .nav-link { color: #FAF8F5; font-size: 16px; }
          
          .section-pad {
            padding: 60px 20px;
          }
          
          .about-grid {
            grid-template-columns: 1fr;
            gap: 40px;
          }
          
          .founder-grid {
            grid-template-columns: 1fr;
            gap: 24px;
            text-align: center;
          }
          
          .founder-photo {
            width: 140px;
            height: 170px;
            margin: 0 auto;
          }
          
          .trust-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          
          .hero-buttons {
            flex-direction: column;
            align-items: stretch;
          }
          
          .hero-buttons .cta-btn {
            text-align: center;
          }
        }
      `}</style>

      {/* ===== NAVIGATION ===== */}
      <nav className={`nav-fixed ${scrolled ? "nav-scrolled" : "nav-top"}`} style={{ padding: "16px 32px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ cursor: "pointer" }} onClick={() => scrollTo("hero")}>
            <span className="dm" style={{ fontSize: 18, fontWeight: 700, color: scrolled ? "#2D4A3E" : "#FAF8F5", letterSpacing: 3, textTransform: "uppercase" }}>
              Untethered
            </span>
            <span className="dm" style={{ fontSize: 18, fontWeight: 700, color: "#C4A77D", letterSpacing: 3, textTransform: "uppercase", marginLeft: 6 }}>
              Giving
            </span>
          </div>

          <div className="desktop-nav" style={{ display: "flex", gap: 32, alignItems: "center" }}>
            {[["About", "about"], ["Approach", "approach"], ["Contact", "contact"]].map(([label, id]) => (
              <button key={id} className="nav-link" onClick={() => scrollTo(id)} style={{ color: scrolled ? "#2D4A3E" : "#FAF8F5" }}>
                {label}
              </button>
            ))}
          </div>

          <button className="mobile-menu" onClick={() => setMenuOpen(true)}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={scrolled ? "#2D4A3E" : "#FAF8F5"} strokeWidth="2">
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="mobile-nav">
          <button onClick={() => setMenuOpen(false)} style={{ position: "absolute", top: 20, right: 24, background: "none", border: "none", cursor: "pointer" }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#FAF8F5" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
          {[["About", "about"], ["Approach", "approach"], ["Contact", "contact"]].map(([label, id]) => (
            <button key={id} className="nav-link" onClick={() => scrollTo(id)} style={{ color: "#FAF8F5" }}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* ===== HERO ===== */}
      <section id="hero" className="hero-section">
        <div style={{ position: "relative", zIndex: 2, maxWidth: 1200, margin: "0 auto", padding: "100px 32px 80px", width: "100%" }}>
          <div style={{ maxWidth: 720 }}>
            <FadeIn>
              <h1 className="playfair" style={{ fontSize: "clamp(36px, 5.5vw, 64px)", color: "#FAF8F5", lineHeight: 1.1, fontWeight: 500 }}>
                Give with <span style={{ color: "#C4A77D", fontStyle: "italic" }}>clarity</span>,
                <br />give with <span style={{ color: "#C4A77D", fontStyle: "italic" }}>purpose</span>
              </h1>
            </FadeIn>
            <FadeIn delay={0.15}>
              <p className="dm" style={{ fontSize: "clamp(16px, 1.8vw, 19px)", color: "rgba(250,248,245,0.7)", lineHeight: 1.7, marginTop: 28, maxWidth: 540, fontWeight: 300 }}>
                We help discerning donors navigate the social sector with confidence, connecting your generosity to trusted organisations creating lasting change.
              </p>
            </FadeIn>
            <FadeIn delay={0.3}>
              <div className="hero-buttons" style={{ marginTop: 44, display: "flex", gap: 16, flexWrap: "wrap" }}>
                <button className="cta-btn" onClick={() => scrollTo("contact")}>Begin Your Journey</button>
                <button className="cta-btn cta-outline" onClick={() => scrollTo("approach")}>Our Approach</button>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ===== ABOUT ===== */}
      <section id="about" style={{ background: "#fff" }}>
        <div className="section-pad">
          <div className="about-grid">
            <FadeIn>
              <div>
                <span className="dm" style={{ color: "#C4A77D", fontSize: 12, letterSpacing: 3, textTransform: "uppercase", fontWeight: 500 }}>
                  About Us
                </span>
                <h2 className="playfair" style={{ fontSize: "clamp(28px, 3.5vw, 42px)", color: "#2D4A3E", lineHeight: 1.2, marginTop: 16 }}>
                  Philanthropy,<br /><span style={{ fontStyle: "italic" }}>reimagined</span>.
                </h2>
                <span className="gold-line" style={{ marginTop: 20 }} />
                <p className="dm" style={{ fontSize: 16, color: "#555", lineHeight: 1.8, marginTop: 24 }}>
                  Untethered Giving was founded on a simple belief: that every act of generosity deserves to create genuine, measurable impact. We serve as a bridge between committed donors and promising grassroots organisations.
                </p>
                <p className="dm" style={{ fontSize: 16, color: "#555", lineHeight: 1.8, marginTop: 16 }}>
                  Our advisory goes beyond writing cheques. We conduct deep due diligence, build lasting relationships with organisations, and ensure your giving is informed, strategic, and deeply fulfilling.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div style={{ background: "linear-gradient(135deg, #2D4A3E, #3d6454)", borderRadius: 12, padding: 48, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: 0, right: 0, width: 120, height: 120, background: "rgba(196,167,125,0.1)", borderRadius: "0 12px 0 100%" }} />
                <div className="playfair" style={{ fontSize: 24, color: "#FAF8F5", fontWeight: 500, lineHeight: 1.4 }}>
                  "The best philanthropy is not about the size of the gift, but the depth of the thought behind it."
                </div>
                <div style={{ marginTop: 40, display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ width: 48, height: 48, borderRadius: "50%", overflow: "hidden", border: "2px solid #C4A77D" }}>
                    <img src="soeleen_photo.jpg" alt="Soeleen Kaur" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                  <div>
                    <div className="dm" style={{ color: "#FAF8F5", fontSize: 14, fontWeight: 600 }}>Soeleen Kaur</div>
                    <div className="dm" style={{ color: "rgba(250,248,245,0.6)", fontSize: 12 }}>Founder, Untethered Giving</div>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ===== FOUNDER ===== */}
      <section style={{ background: "#FAF8F5" }}>
        <div className="section-pad">
          <FadeIn>
            <div style={{ textAlign: "center", maxWidth: 700, margin: "0 auto" }}>
              <span className="dm" style={{ color: "#C4A77D", fontSize: 12, letterSpacing: 3, textTransform: "uppercase", fontWeight: 500 }}>
                The Founder
              </span>
              <h2 className="playfair" style={{ fontSize: "clamp(28px, 3.5vw, 42px)", color: "#2D4A3E", lineHeight: 1.2, marginTop: 16 }}>
                Led by <span style={{ fontStyle: "italic" }}>experience</span>
              </h2>
              <span className="gold-line" style={{ margin: "20px auto 0" }} />
            </div>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div className="founder-grid">
              <div className="founder-photo">
                <img src="soeleen_photo.jpg" alt="Soeleen Kaur" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <div>
                <h3 className="playfair" style={{ fontSize: 26, color: "#2D4A3E" }}>Soeleen</h3>
                <p className="dm" style={{ fontSize: 14, color: "#C4A77D", fontWeight: 500, marginTop: 4, letterSpacing: 0.5 }}>
                  MPhil in Education, University of Cambridge
                </p>
                <p className="dm" style={{ fontSize: 15, color: "#555", lineHeight: 1.8, marginTop: 16 }}>
                  Soeleen is an experienced education practitioner and researcher, with a deep understanding of education leadership in low-income communities in India. Her work spans both classrooms and the field, from teaching in under-resourced schools to supporting communities and advising on systemic education reform.
                </p>
                <p className="dm" style={{ fontSize: 15, color: "#555", lineHeight: 1.8, marginTop: 16 }}>
                  With firsthand knowledge of India's social sector, she identifies organisations with genuine impact potential and connects them with donors who share their vision.
                </p>
                <div style={{ marginTop: 20 }}>
                  <a 
                    href="https://www.linkedin.com/in/soeleen" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#2D4A3E", textDecoration: "none", fontSize: 14, fontWeight: 500 }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#2D4A3E">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    Connect on LinkedIn
                  </a>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="approach" style={{ background: "#fff" }}>
        <div className="section-pad">
          <FadeIn>
            <div style={{ textAlign: "center", maxWidth: 600, margin: "0 auto" }}>
              <span className="dm" style={{ color: "#C4A77D", fontSize: 12, letterSpacing: 3, textTransform: "uppercase", fontWeight: 500 }}>
                Our Approach
              </span>
              <h2 className="playfair" style={{ fontSize: "clamp(28px, 3.5vw, 42px)", color: "#2D4A3E", lineHeight: 1.2, marginTop: 16 }}>
                From intention to <span style={{ fontStyle: "italic" }}>impact</span>
              </h2>
              <span className="gold-line" style={{ margin: "20px auto 0" }} />
            </div>
          </FadeIn>

          <div style={{ maxWidth: 640, margin: "56px auto 0", display: "flex", flexDirection: "column", gap: 48 }}>
            {[
              { step: "01", title: "Discovery", desc: "We begin with a conversation to understand your values, passions, and giving goals." },
              { step: "02", title: "Curation", desc: "We present a handpicked portfolio of rigorously vetted organisations aligned with your interests." },
              { step: "03", title: "Impact", desc: "We provide transparent, ongoing reporting so you can see exactly how your giving creates change." },
              { step: "04", title: "Connection", desc: "We facilitate introductions, site visits, and deep engagement with the organisations you choose." },
            ].map(({ step, title, desc }, i) => (
              <FadeIn key={step} delay={i * 0.1}>
                <div className="process-step" data-step={step}>
                  <h3 className="playfair" style={{ fontSize: 22, color: "#2D4A3E", fontWeight: 600 }}>{title}</h3>
                  <p className="dm" style={{ fontSize: 15, color: "#666", lineHeight: 1.7, marginTop: 8 }}>{desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TRUST / WHY US ===== */}
      <section style={{ background: "#FAF8F5" }}>
        <div className="section-pad">
          <FadeIn>
            <div style={{ textAlign: "center", maxWidth: 600, margin: "0 auto" }}>
              <span className="dm" style={{ color: "#C4A77D", fontSize: 12, letterSpacing: 3, textTransform: "uppercase", fontWeight: 500 }}>
                Why Trust Us
              </span>
              <h2 className="playfair" style={{ fontSize: "clamp(28px, 3.5vw, 42px)", color: "#2D4A3E", lineHeight: 1.2, marginTop: 16 }}>
                Built on <span style={{ fontStyle: "italic" }}>integrity</span>
              </h2>
              <span className="gold-line" style={{ margin: "20px auto 0" }} />
            </div>
          </FadeIn>

          <div className="trust-grid">
            {[
              { title: "Due Diligence", desc: "We get in touch with the team members of every organisation, and understand their work thoroughly before recommending them to you." },
              { title: "Transparent Process", desc: "Clear fees, timely updates, and open communication, so you can give with confidence." },
            ].map((item, i) => (
              <FadeIn key={item.title} delay={i * 0.1}>
                <div style={{ display: "flex", gap: 16 }}>
                  <div style={{ minWidth: 32, height: 32, borderRadius: "50%", background: "#2D4A3E", display: "flex", alignItems: "center", justifyContent: "center", marginTop: 2 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C4A77D" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
                  </div>
                  <div>
                    <h4 className="dm" style={{ fontSize: 16, color: "#2D4A3E", fontWeight: 600 }}>{item.title}</h4>
                    <p className="dm" style={{ fontSize: 14, color: "#666", lineHeight: 1.7, marginTop: 6 }}>{item.desc}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section id="contact" style={{ background: "linear-gradient(165deg, #2D4A3E, #1a3029)", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 0%, rgba(196,167,125,0.08) 0%, transparent 60%)" }} />
        <div className="section-pad" style={{ position: "relative", zIndex: 2, textAlign: "center" }}>
          <FadeIn>
            <span className="dm" style={{ color: "#C4A77D", fontSize: 12, letterSpacing: 3, textTransform: "uppercase", fontWeight: 500 }}>
              Contact Us
            </span>
            <h2 className="playfair" style={{ fontSize: "clamp(30px, 4vw, 48px)", color: "#FAF8F5", lineHeight: 1.2, marginTop: 16 }}>
              Ready to give with <span style={{ fontStyle: "italic", color: "#C4A77D" }}>intention</span>?
            </h2>
            <p className="dm" style={{ fontSize: 16, color: "rgba(250,248,245,0.6)", lineHeight: 1.7, marginTop: 20, maxWidth: 500, margin: "20px auto 0" }}>
              We'd love to hear from you. Whether you're exploring philanthropy for the first time or looking to deepen your impact, we're here to help.
            </p>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div style={{ marginTop: 48, textAlign: "center" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C4A77D" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <a 
                  href="mailto:info@untetheredgiving.org" 
                  className="dm" 
                  style={{ fontSize: 20, color: "#FAF8F5", textDecoration: "none", fontWeight: 500 }}
                >
                  info@untetheredgiving.org
                </a>
              </div>
              <div>
                <a 
                  href="mailto:info@untetheredgiving.org"
                  className="cta-btn"
                  style={{ textDecoration: "none" }}
                >
                  Start a Conversation
                </a>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer style={{ background: "#1a2a23", padding: "40px 32px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div>
            <span className="dm" style={{ fontSize: 14, fontWeight: 700, color: "#FAF8F5", letterSpacing: 2, textTransform: "uppercase" }}>
              Untethered
            </span>
            <span className="dm" style={{ fontSize: 14, fontWeight: 700, color: "#C4A77D", letterSpacing: 2, textTransform: "uppercase", marginLeft: 4 }}>
              Giving
            </span>
          </div>
          <p className="dm" style={{ fontSize: 12, color: "rgba(250,248,245,0.3)" }}>
            © 2026 Untethered Giving. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
